import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useStore } from '@/context/StoreContext';
import { api, productPath } from '@/lib/api';
import { toast } from 'sonner';
import { Seo } from '@/components/Seo';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { authField, authLabel } from '@/components/auth/AuthForms';
import { WishlistButton } from '@/components/WishlistButton';
import { Search, Package, MapPin, Heart, User, LogOut, ChevronDown, Plus, Trash2, Star, BellRing, X } from 'lucide-react';

const STATUS_STYLE = {
  pending: 'border-nw-warmgrey/40 text-nw-warmgrey', confirmed: 'border-nw-gold/60 text-nw-gold',
  preparing: 'border-nw-gold/60 text-nw-gold', shipped: 'border-nw-ivory/50 text-nw-ivory',
  delivered: 'border-nw-gold text-nw-gold', cancelled: 'border-nw-warmgrey/30 text-nw-warmgrey/60',
};

const fmtDate = (iso, lang) => new Date(iso).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', { day: '2-digit', month: 'long', year: 'numeric' });

export default function Account() {
  const { t, lang, customer, customerLoading, logoutCustomer } = useStore();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'orders';

  useEffect(() => { if (!customerLoading && !customer) navigate('/account/login?next=/account', { replace: true }); }, [customer, customerLoading]); // eslint-disable-line

  if (customerLoading || !customer) return <div className="nw-container py-32 text-center text-nw-warmgrey">{t.common.loading}</div>;

  const firstName = customer.full_name?.split(' ')[0] || '';
  const tabs = [
    { key: 'orders', icon: Package, label: t.account.tabs.orders },
    { key: 'addresses', icon: MapPin, label: t.account.tabs.addresses },
    { key: 'cellar', icon: Heart, label: t.account.tabs.cellar },
    { key: 'profile', icon: User, label: t.account.tabs.profile },
  ];

  return (
    <div className="bg-nw-black">
      <Seo title={t.account.title} description={t.account.signInSub} path="/account" noindex />
      <div className="nw-container py-12 lg:py-16">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <div className="nw-eyebrow mb-2">{t.account.title}</div>
            <h1 data-testid="account-greeting" className="font-serif text-4xl sm:text-5xl text-nw-ivory">{t.account.greeting}, {firstName}</h1>
            <p className="mt-2 text-sm text-nw-warmgrey">{customer.email} · {t.account.memberSince} {fmtDate(customer.created_at, lang)}</p>
          </div>
          <button data-testid="account-logout-button" onClick={() => { logoutCustomer(); navigate('/'); }} className="inline-flex items-center gap-2 text-[11px] tracking-luxe uppercase nw-link"><LogOut size={14} /> {t.account.logout}</button>
        </div>

        <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
          <TabsList className="bg-nw-espresso border border-nw-gold/20 h-auto p-1 flex flex-wrap justify-start gap-1">
            {tabs.map((tb) => (
              <TabsTrigger key={tb.key} value={tb.key} data-testid={`account-tab-${tb.key}`}
                className="data-[state=active]:bg-nw-gold data-[state=active]:text-nw-black text-nw-warmgrey text-[11px] tracking-wide2 uppercase px-4 h-10 gap-2">
                <tb.icon size={14} /> {tb.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="orders" className="mt-8"><OrdersTab /></TabsContent>
          <TabsContent value="addresses" className="mt-8"><AddressesTab /></TabsContent>
          <TabsContent value="cellar" className="mt-8"><CellarTab /></TabsContent>
          <TabsContent value="profile" className="mt-8"><ProfileTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- ORDERS */
function OrdersTab() {
  const { t, lang, formatPrice } = useStore();
  const [filters, setFilters] = useState({ search: '', date_from: '', date_to: '', min_total: '', max_total: '', sort: 'date_desc' });
  const [orders, setOrders] = useState(null);
  const [open, setOpen] = useState(null);
  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const active = Object.entries(filters).some(([k, v]) => k !== 'sort' && v);

  useEffect(() => {
    const h = setTimeout(() => {
      const q = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ''));
      api.customerOrders(q).then((d) => setOrders(d.items)).catch(() => setOrders([]));
    }, 300);
    return () => clearTimeout(h);
  }, [filters]);

  const small = 'w-full bg-nw-black/60 border border-nw-gold/25 rounded-[3px] px-3 h-10 text-nw-ivory placeholder:text-nw-warmgrey/50 focus:outline-none focus:border-nw-gold text-sm';

  return (
    <div data-testid="account-orders">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-serif text-2xl text-nw-ivory">{t.account.ordersTitle}</h2>
        {active && <button data-testid="orders-reset-filters" onClick={() => setFilters({ search: '', date_from: '', date_to: '', min_total: '', max_total: '', sort: 'date_desc' })} className="text-[11px] text-nw-gold hover:underline inline-flex items-center gap-1"><X size={12} /> {t.account.reset}</button>}
      </div>
      <div className="bg-nw-espresso border border-nw-gold/15 p-4 grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6">
        <div className="col-span-2 relative">
          <Search size={14} className="absolute left-3 top-3 text-nw-warmgrey" />
          <input data-testid="orders-search-input" className={small + ' pl-9'} placeholder={t.account.searchOrders} value={filters.search} onChange={set('search')} />
        </div>
        <input data-testid="orders-date-from" type="date" aria-label={t.account.dateFrom} className={small} value={filters.date_from} onChange={set('date_from')} />
        <input data-testid="orders-date-to" type="date" aria-label={t.account.dateTo} className={small} value={filters.date_to} onChange={set('date_to')} />
        <input data-testid="orders-min-total" type="number" min="0" placeholder={`${t.account.minTotal} €`} className={small} value={filters.min_total} onChange={set('min_total')} />
        <div className="flex gap-3">
          <input data-testid="orders-max-total" type="number" min="0" placeholder={`${t.account.maxTotal} €`} className={small} value={filters.max_total} onChange={set('max_total')} />
        </div>
        <div className="col-span-2 lg:col-span-6 flex justify-end">
          <Select value={filters.sort} onValueChange={set('sort')}>
            <SelectTrigger data-testid="orders-sort-select" className="w-[200px] h-9 bg-transparent border-nw-gold/25 text-nw-ivory text-xs tracking-wide2 uppercase"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-nw-espresso border-nw-gold/25 text-nw-ivory">
              <SelectItem value="date_desc">{t.account.sortDateDesc}</SelectItem>
              <SelectItem value="date_asc">{t.account.sortDateAsc}</SelectItem>
              <SelectItem value="total_desc">{t.account.sortTotalDesc}</SelectItem>
              <SelectItem value="total_asc">{t.account.sortTotalAsc}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {orders === null ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <div key={i} className="h-20 bg-nw-espresso animate-pulse" />)}</div>
      ) : orders.length === 0 ? (
        <div data-testid="orders-empty" className="py-16 text-center border border-nw-gold/15 bg-nw-espresso">
          <Package size={32} strokeWidth={1} className="text-nw-gold mx-auto" />
          <p className="mt-4 text-nw-warmgrey text-sm">{active ? t.account.noMatch : t.account.ordersEmpty}</p>
          {!active && <Link to="/collections" className="nw-btn-gold mt-6">{t.account.browse}</Link>}
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => {
            const isOpen = open === o.id;
            const n = o.items.reduce((s, i) => s + i.quantity, 0);
            return (
              <div key={o.id} data-testid={`order-row-${o.order_id}`} className="border border-nw-gold/15 bg-nw-espresso">
                <button onClick={() => setOpen(isOpen ? null : o.id)} className="w-full text-left p-5 grid grid-cols-2 md:grid-cols-[1.4fr_1fr_1fr_1fr_auto] gap-3 items-center hover:bg-nw-ivory/[0.02] transition-colors">
                  <div>
                    <div className="text-[10px] tracking-luxe uppercase text-nw-warmgrey">{t.account.order}</div>
                    <div className="text-nw-gold mt-0.5">{o.order_id}</div>
                  </div>
                  <div>
                    <div className="text-[10px] tracking-luxe uppercase text-nw-warmgrey">{t.account.date}</div>
                    <div className="text-nw-ivory text-sm mt-0.5">{fmtDate(o.created_at, lang)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] tracking-luxe uppercase text-nw-warmgrey">{t.account.status}</div>
                    <span className={`inline-block mt-1 text-[10px] tracking-wide2 uppercase border px-2 py-0.5 ${STATUS_STYLE[o.status] || STATUS_STYLE.pending}`}>{t.account.statuses[o.status] || o.status}</span>
                  </div>
                  <div>
                    <div className="text-[10px] tracking-luxe uppercase text-nw-warmgrey">{t.account.total} · {n} {t.account.items}</div>
                    <div className="font-serif text-xl text-nw-gold mt-0.5">{formatPrice(o.total_eur)}</div>
                  </div>
                  <ChevronDown size={18} className={`text-nw-warmgrey justify-self-end transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && (
                  <div className="border-t border-nw-gold/15 p-5 grid md:grid-cols-[1fr_280px] gap-6 animate-fade-in">
                    <div className="space-y-3">
                      {o.items.map((it) => (
                        <div key={it.product_id} className="flex gap-4 items-center">
                          <Link to={`/product/${it.slug || it.product_id}`} className="w-12 h-16 bg-nw-black border border-nw-gold/15 overflow-hidden shrink-0">
                            {it.image && <img src={it.image} alt={it.name} className="w-full h-full object-cover" loading="lazy" />}
                          </Link>
                          <div className="flex-1 min-w-0">
                            <Link to={`/product/${it.slug || it.product_id}`} className="text-sm text-nw-ivory hover:text-nw-gold transition-colors">{it.name}</Link>
                            <div className="text-[11px] text-nw-warmgrey">{it.reference_id} · × {it.quantity}</div>
                          </div>
                          <div className="text-sm text-nw-gold">{formatPrice(it.line_total_eur)}</div>
                        </div>
                      ))}
                    </div>
                    <div className="text-sm space-y-2 border-l border-nw-gold/15 pl-6">
                      <div className="flex justify-between"><span className="text-nw-warmgrey">{t.cart.subtotal}</span><span className="text-nw-ivory">{formatPrice(o.subtotal_eur)}</span></div>
                      <div className="flex justify-between"><span className="text-nw-warmgrey">{t.cart.shipping}</span><span className="text-nw-ivory">{formatPrice(o.shipping_eur)}</span></div>
                      {o.insurance && <div className="flex justify-between"><span className="text-nw-warmgrey">{t.cart.insuranceLabel}</span><span className="text-nw-ivory">{formatPrice(o.insurance_eur)}</span></div>}
                      <div className="pt-3 mt-3 border-t border-nw-gold/15">
                        <div className="text-[10px] tracking-luxe uppercase text-nw-warmgrey">{t.checkout.address}</div>
                        <div className="text-nw-ivory text-xs mt-1 leading-relaxed">{o.delivery_address?.line1}{o.delivery_address?.line2 ? `, ${o.delivery_address.line2}` : ''}<br />{o.delivery_address?.postal_code} {o.delivery_address?.city}, {o.delivery_address?.country}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------- ADDRESSES */
const EMPTY_ADDR = { label: '', first_name: '', last_name: '', line1: '', line2: '', city: '', postal_code: '', country: '', state: '', phone: '', is_default: false };

export function AddressForm({ initial, onSaved, onCancel }) {
  const { t } = useStore();
  const [a, setA] = useState({ ...EMPTY_ADDR, ...initial });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setA((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const saved = initial?.id ? await api.customerUpdateAddress(initial.id, a) : await api.customerAddAddress(a);
      toast.success(t.account.saved); onSaved(saved);
    } catch { toast.error(t.common.error); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={save} className="space-y-4">
      <div><label className={authLabel}>{t.account.label}</label><input data-testid="address-label" className={authField} value={a.label} onChange={set('label')} /></div>
      <div className="grid grid-cols-2 gap-4">
        <div><label className={authLabel}>{t.checkout.firstName}</label><input className={authField} value={a.first_name} onChange={set('first_name')} /></div>
        <div><label className={authLabel}>{t.checkout.lastName}</label><input className={authField} value={a.last_name} onChange={set('last_name')} /></div>
      </div>
      <div><label className={authLabel}>{t.checkout.line1}</label><input data-testid="address-line1" required className={authField} value={a.line1} onChange={set('line1')} /></div>
      <div><label className={authLabel}>{t.checkout.line2}</label><input className={authField} value={a.line2} onChange={set('line2')} /></div>
      <div className="grid grid-cols-2 gap-4">
        <div><label className={authLabel}>{t.checkout.city}</label><input data-testid="address-city" required className={authField} value={a.city} onChange={set('city')} /></div>
        <div><label className={authLabel}>{t.checkout.postal}</label><input data-testid="address-postal" required className={authField} value={a.postal_code} onChange={set('postal_code')} /></div>
        <div><label className={authLabel}>{t.checkout.country}</label><input data-testid="address-country" required className={authField} value={a.country} onChange={set('country')} /></div>
        <div><label className={authLabel}>{t.checkout.phone}</label><input className={authField} value={a.phone} onChange={set('phone')} /></div>
      </div>
      <label className="flex items-center gap-3 text-sm text-nw-warmgrey"><Switch checked={a.is_default} onCheckedChange={set('is_default')} className="data-[state=checked]:bg-nw-gold" /> {t.account.setDefault}</label>
      <div className="flex gap-3 pt-2">
        <button type="submit" data-testid="address-save-button" disabled={saving} className="nw-btn-gold-solid flex-1 h-11">{t.account.save}</button>
        {onCancel && <button type="button" onClick={onCancel} className="nw-btn-gold h-11">{t.account.cancel}</button>}
      </div>
    </form>
  );
}

function AddressesTab() {
  const { t, refreshCustomer, customer } = useStore();
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const addresses = customer?.addresses || [];

  const remove = async (id) => { await api.customerDeleteAddress(id); await refreshCustomer(); toast.success(t.account.saved); };
  const makeDefault = async (a) => { await api.customerUpdateAddress(a.id, { ...a, is_default: true }); await refreshCustomer(); };

  return (
    <div data-testid="account-addresses">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-serif text-2xl text-nw-ivory">{t.account.addressesTitle}</h2>
        <Dialog open={dialog} onOpenChange={(v) => { setDialog(v); if (!v) setEditing(null); }}>
          <DialogTrigger asChild><button data-testid="address-add-button" onClick={() => setEditing(null)} className="nw-btn-gold h-10"><Plus size={14} /> {t.account.addAddress}</button></DialogTrigger>
          <DialogContent className="bg-nw-espresso border-nw-gold/25 text-nw-ivory max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-serif text-2xl text-nw-gold">{t.account.addAddress}</DialogTitle></DialogHeader>
            <AddressForm initial={editing} onSaved={async () => { await refreshCustomer(); setDialog(false); setEditing(null); }} />
          </DialogContent>
        </Dialog>
      </div>
      {addresses.length === 0 ? (
        <div data-testid="addresses-empty" className="py-16 text-center border border-nw-gold/15 bg-nw-espresso">
          <MapPin size={32} strokeWidth={1} className="text-nw-gold mx-auto" />
          <p className="mt-4 text-nw-warmgrey text-sm">{t.account.addressesEmpty}</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {addresses.map((a) => (
            <div key={a.id} data-testid={`address-card-${a.id}`} className={`border p-5 bg-nw-espresso relative ${a.is_default ? 'border-nw-gold/60' : 'border-nw-gold/15'}`}>
              {a.is_default && <span className="absolute top-3 right-3 text-[9px] tracking-luxe uppercase text-nw-gold inline-flex items-center gap-1"><Star size={10} className="fill-nw-gold" /> {t.account.default}</span>}
              <div className="text-[10px] tracking-luxe uppercase text-nw-warmgrey">{a.label || a.city}</div>
              <div className="mt-2 text-sm text-nw-ivory leading-relaxed">
                {(a.first_name || a.last_name) && <div>{a.first_name} {a.last_name}</div>}
                <div>{a.line1}</div>{a.line2 && <div>{a.line2}</div>}
                <div>{a.postal_code} {a.city}{a.state ? `, ${a.state}` : ''}</div>
                <div>{a.country}</div>{a.phone && <div className="text-nw-warmgrey">{a.phone}</div>}
              </div>
              <div className="mt-4 flex items-center gap-4 text-[11px] tracking-wide uppercase">
                <button data-testid={`address-edit-${a.id}`} onClick={() => { setEditing(a); setDialog(true); }} className="nw-link">{t.account.edit}</button>
                {!a.is_default && <button onClick={() => makeDefault(a)} className="nw-link">{t.account.setDefault}</button>}
                <button data-testid={`address-delete-${a.id}`} onClick={() => remove(a.id)} className="text-nw-warmgrey hover:text-nw-gold inline-flex items-center gap-1 transition-colors"><Trash2 size={12} /> {t.account.delete}</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------- CELLAR */
function CellarTab() {
  const { t, formatPrice, wishlistIds } = useStore();
  const [items, setItems] = useState(null);
  useEffect(() => { api.wishlist().then((d) => setItems(d.items)).catch(() => setItems([])); }, [wishlistIds]);

  return (
    <div data-testid="account-cellar">
      <h2 className="font-serif text-2xl text-nw-ivory">{t.account.cellarTitle}</h2>
      <p className="mt-1 text-sm text-nw-warmgrey mb-6">{t.account.cellarSub}</p>
      {items === null ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">{[0, 1, 2, 3].map((i) => <div key={i} className="bg-nw-espresso animate-pulse" style={{ aspectRatio: '3/4' }} />)}</div>
      ) : items.length === 0 ? (
        <div data-testid="cellar-empty" className="py-16 text-center border border-nw-gold/15 bg-nw-espresso">
          <Heart size={32} strokeWidth={1} className="text-nw-gold mx-auto" />
          <p className="mt-4 text-nw-warmgrey text-sm max-w-sm mx-auto">{t.account.cellarEmpty}</p>
          <Link to="/collections?rarity=collector" className="nw-btn-gold mt-6">{t.account.browse}</Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {items.map(({ product: p, notify }) => (
            <div key={p.id} data-testid={`cellar-item-${p.reference_id}`} className="group">
              <div className="relative overflow-hidden bg-nw-espresso border border-nw-gold/15" style={{ aspectRatio: '3/4' }}>
                <Link to={productPath(p)}>{p.images?.[0] && <img src={p.images[0]} alt={p.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />}</Link>
                <WishlistButton product={p} className="absolute top-3 right-3" />
                {notify && <span className="absolute bottom-3 left-3 text-[9px] tracking-luxe uppercase text-nw-gold bg-nw-black/70 px-2 py-1 inline-flex items-center gap-1"><BellRing size={10} /> {t.account.alertActive}</span>}
              </div>
              <div className="pt-3 text-center">
                <Link to={productPath(p)} className="font-serif text-nw-ivory hover:text-nw-gold transition-colors leading-snug block">{p.name}</Link>
                <div className="mt-1 text-nw-gold text-sm">{formatPrice(p.price_eur)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------- PROFILE */
function ProfileTab() {
  const { t, customer, refreshCustomer, setLang } = useStore();
  const [f, setF] = useState({ full_name: customer.full_name || '', phone: customer.phone || '', lang: customer.lang || 'fr' });
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.customerUpdate(f); await refreshCustomer(); setLang(f.lang); toast.success(t.account.saved); }
    catch { toast.error(t.common.error); }
    finally { setSaving(false); }
  };
  return (
    <div data-testid="account-profile" className="max-w-lg">
      <h2 className="font-serif text-2xl text-nw-ivory mb-6">{t.account.profileTitle}</h2>
      <form onSubmit={save} className="space-y-5 bg-nw-espresso border border-nw-gold/15 p-6 sm:p-8">
        <div><label className={authLabel}>{t.account.fullName}</label><input data-testid="profile-name-input" required className={authField} value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} /></div>
        <div><label className={authLabel}>{t.account.email}</label><input className={authField + ' opacity-60'} value={customer.email} disabled /></div>
        <div><label className={authLabel}>{t.account.phone}</label><input data-testid="profile-phone-input" className={authField} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
        <div>
          <label className={authLabel}>{t.account.language}</label>
          <Select value={f.lang} onValueChange={(v) => setF({ ...f, lang: v })}>
            <SelectTrigger className={authField}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-nw-espresso border-nw-gold/25 text-nw-ivory"><SelectItem value="fr">Français</SelectItem><SelectItem value="en">English</SelectItem></SelectContent>
          </Select>
        </div>
        <button type="submit" data-testid="profile-save-button" disabled={saving} className="nw-btn-gold-solid w-full h-12">{t.account.save}</button>
      </form>
    </div>
  );
}
